import { PLUGIN_NAME } from '../../plugins/config';
import DebugUANC from '../../../core/version/debug';
import { isArrayType, isEmpty, isObjectType, isStringType } from '../../../helpers/js-utils';
import { IConfig, IDetection, IExSettings, IPluginInfo, IServices, ISettings, ISlot, MappingType, SizeType } from '../../../uanc.types';
import { IDefinedAdUnits, IDFPService, IDFPServiceSlot } from './service-dfp.types';
import { addSlotEvent, buildServiceSlotsConfig, findSlotByAdUnitName, getSlotsByAdServer, groupExecution } from '../ad-services.utils';
import { AD_SERVERS, AD_STATUS, MULTI_PLACE_SLOTS } from '../../../configs/constants';
import SizeMappingBuilder = googletag.SizeMappingBuilder;
import { getLazyLoadingSlots, getNotLazyLoadingSlots, verifySlotsToDefined } from '../embedding.utils';
import { observeElement } from '../../../helpers/elements-visibility';
import { IAdsToEmbed, TargetingParamsType, TargetingType } from '../ad-services.types';
import { requestForBids } from '../../prebid/prebid';
import { dsaGAM } from './label-dsa';
import { serviceDFPUtils } from './service-dfp';
import Slot = googletag.Slot;
import { iwaAddEvent } from '../../iwa-add-event';
import SlotRenderEndedEvent = googletag.events.SlotRenderEndedEvent;
import SlotRequestedEvent = googletag.events.SlotRequestedEvent;

/**
 * Zwraca nazwę slotu, dla którego zdefiniowana została jednostka reklamowa.
 *
 * @param {IAdsToEmbed} adsToEmbed - nazwa slotu
 * @param {string} adUnitName - jednostka reklamowej
 * @returns {string|null} nazwa slotu
 */
export const getDefinedSlotName = (adsToEmbed: IAdsToEmbed, adUnitName: string): string | null => {
    return (
        Object.keys(adsToEmbed).find((name) => adsToEmbed[name].find((adToEmbed) => adToEmbed.placementIds.includes(adUnitName))) ?? null
    );
};

/**
 * Ustawia/aktualizuje targeting dla slotu. Wykorzystywane podczas odświeżania reklam.
 *
 * @param {string} slotName - nazwa slotu
 * @param {googletag} adUnit - jednostka reklamowej
 * @param {TargetingType} targeting - ustawienie targetowania
 * @returns {void}
 */
export const setTargetingBeforeRefresh = (slotName: string, adUnit: googletag.Slot, targeting: TargetingType): void => {
    const targetingKey: string = targeting[0];
    const targetingParams: TargetingParamsType = targeting[1];
    googletag.cmd.push(() => {
        adUnit.setTargeting(targetingKey, targetingParams);
    });

    window.addEventListener(`Inpl.${slotName}.loaded`, function adLoadedHandler() {
        googletag.cmd.push(() => {
            adUnit.clearTargeting(targeting[0]);
        });
        window.removeEventListener(`Inpl.${slotName}.loaded`, adLoadedHandler);
    });
};

/**
 * Zwraca jednostkę reklamową jeżeli istnieje.
 *
 * @param {ServiceSlotType} slotConfig - slot serwisowy
 * @param {string} placementName - nazwa placementu / jednostki reklamowej
 * @returns {googletag.Slot | null} adUnit
 */
export const getExistingAdUnit = (slotConfig: IDFPServiceSlot, placementName: string): googletag.Slot | null => {
    const { services } = window.Inpl.Ad;
    const serviceDFP = services?.DFPService;
    if (!serviceDFP || !(slotConfig.renderedPlacements && placementName in slotConfig.renderedPlacements)) return null; // sprawdzenie czy znajduje się w konfiguracji slotu jako wyrenderowany slot

    return serviceDFP.adUnits[placementName] || null;
};

/**
 *  Przygotowuje domyślną/startową konfigurację DFPServices. Uzupełnia konfiguracje slotów do osadzenia.
 *
 * @returns {IDFPService} konfiguracja DPFServices
 */
export const prepareInitialDFPServiceConfig = (): IDFPService => {
    const { config, services } = window.Inpl.Ad;

    let configDFPServices: IDFPService = {
        isEnabled: true,
        mapping: {},
        adUnits: {},
        type: 'DFP',
        keywords: config!.keywords.DFP ? [config!.keywords.DFP] : [],
        slots: [],
        inView: {},
        linkSponsPendingItems: {
            data: {},
            dataRequests: {},
        },
        groupExecutionItem: {},
    };

    if (services?.DFPService) {
        configDFPServices = {
            ...services.DFPService,
            ...configDFPServices,
        };
    }

    const slotsDFP: ISlot[] = getSlotsByAdServer(AD_SERVERS.DFP, config!.slots);
    configDFPServices.slots = buildServiceSlotsConfig(AD_SERVERS.DFP, slotsDFP) as IDFPServiceSlot[];
    return configDFPServices;
};

/**
 * Dodaje do kolejki GPT wywołanie metody destroySlots - usuwa wszystkie aktywne sloty reklamowe utworzonych przez googletag.
 *
 * @returns {void}
 */
export const destroyAllSlots = (): void => {
    googletag.cmd.push(() => googletag.destroySlots());
};

/**
 * Konfiguracja GPT i zarządzania reklamami.
 *
 * @returns {void}
 */
export const setGPTConfig = (): void => {
    // DFP server settings
    googletag.cmd.push(() => {
        // disables automatic ad loading when the page is initially loaded
        googletag.pubads().disableInitialLoad();
        // enables single request mode for fetching multiple ads at the same time
        googletag.pubads().enableSingleRequest();
        // collapsing empty divs
        googletag.pubads().collapseEmptyDivs();
        // set page_url, fixes ads on dev
        googletag
            .pubads()
            .set('page_url', typeof location === 'object' && location.host.indexOf('.inpl.work') == -1 ? location.href : 'interia.pl');
        // enables all GPT services that have been defined for ad slots on the page
        googletag.enableServices();
    });
};

/**
 * Metoda ustawia wartości targetowania reklam publikowanych na stronie pod względem keywords konkretnych pluginów.
 *
 * @param {PLUGIN_NAME} pluginName - nazwa pluginu
 * @param {string} key - klucz targetowania, nazwa parametru kierowania reklam
 * @returns {void}
 */
const setTargetingPluginKeywords = (pluginName: PLUGIN_NAME, key: string): void => {
    const { plugins, services, UANC } = window.Inpl.Ad;
    const customKeywords = services?.DFPService?.keywords || [];

    if (plugins?.[pluginName]) {
        const { isEnabled, keywords } = plugins?.[pluginName] as IPluginInfo;

        if (pluginName === PLUGIN_NAME.TCF_KEYWORDS && key === 'ltd' && isObjectType(keywords) && 'ltd' in keywords) {
            googletag.cmd.push(() => {
                googletag.pubads().setTargeting(key, [`${keywords['ltd']}`]);
            });
        } else if (isEnabled && keywords && !isEmpty(keywords) && isArrayType(keywords)) {
            if (pluginName === PLUGIN_NAME.CUSTOM_KEYWORDS) {
                googletag.cmd.push(() => {
                    googletag.pubads().setTargeting(key, [...keywords, ...customKeywords]);
                });
            } else {
                googletag.cmd.push(() => {
                    googletag.pubads().setTargeting(key, keywords);
                });
            }
        }
    } else {
        UANC instanceof DebugUANC && UANC.warn(`Plugin "${pluginName}" is not enabled`);
    }
};

/**
 * Metoda ustawia globalne targetowanie dla wszystkich reklam. Bierze pod uwagę różne parametry, np: cid, pid, host czy adultContent. Ustawia targetowania również pod względem keywords pluginów.
 *
 * @returns {void}
 */
export const setGPTTargeting = (): void => {
    const { detection, config, settings, exSettings } = window.Inpl.Ad;

    const { params, pageURI, pageURI2, host, hostName } = detection as IDetection;
    const { cid, pid } = config as IConfig;
    const serviceSettings: IExSettings | ISettings = !isEmpty(exSettings) ? (exSettings as IExSettings) : (settings as ISettings);

    googletag.cmd.push(() => {
        // set keywords if url have params
        if (params) {
            Object.keys(params).forEach((param) => {
                if (params[param]) {
                    googletag.pubads().setTargeting(param, params[param]);
                }
            });
        }
        // set targetting for url params
        if ('urlParams' in serviceSettings && !isEmpty(serviceSettings.urlParams)) {
            const urlParams = serviceSettings.urlParams as { [key: string]: string };
            Object.keys(urlParams).forEach((param) => {
                if (urlParams[param]) {
                    googletag.pubads().setTargeting(param, urlParams[param]);
                }
            });
        }

        // set tragetting for page URI
        googletag.pubads().setTargeting('pageUri', pageURI);
        googletag.pubads().setTargeting('pageUri2', pageURI2);
        // set targetting CID/PID
        googletag.pubads().setTargeting('cid', cid);
        googletag.pubads().setTargeting('pid', pid);

        // set targeting for adult content
        if (serviceSettings.adultContent) {
            googletag.pubads().setTargeting('adultContent', '1');
        }
        // set targeting for video player
        if (serviceSettings.videoPlayer) {
            googletag.pubads().setTargeting('vp', '1');
        }

        // set targetting for host
        googletag.pubads().setTargeting('host', host);
        googletag.pubads().setTargeting('hostname', hostName);

        // set targetting for plugins
        setTargetingPluginKeywords(PLUGIN_NAME.RODO_KEYWORD, 'rodo');
        setTargetingPluginKeywords(PLUGIN_NAME.CUSTOM_KEYWORDS, 'customkeywords');
        setTargetingPluginKeywords(PLUGIN_NAME.NETSPRINT, 'pi');
        setTargetingPluginKeywords(PLUGIN_NAME.TCF_KEYWORDS, 'ltd');

        if ('customParams' in serviceSettings && serviceSettings.customParams?.DFP) {
            for (const param in serviceSettings.customParams.DFP) {
                googletag.pubads().setTargeting(param, serviceSettings.customParams.DFP[param]);
            }
        }
    });
};

/**
 * Definiowanie mappingu dla slotu. Wykorzystywana metoda Google Publisher Tag - sizeMapping.
 * Określa rozmiary reklamy w zależności od rozdzielczości ekranu lub wielkości okna przeglądarki.
 *
 * @param {MappingType[]} slotMapping - rozmiary mapping dla slotu
 * @param {SizeType[]} slotSizes - rozmiary slotu
 * @returns {SizeMappingArray} przygotowany mapping slotu dla GPT
 */
const buildMapping = (slotMapping: MappingType[], slotSizes: SizeType[]): googletag.SizeMappingArray => {
    const mapping: SizeMappingBuilder = googletag.sizeMapping();

    if (slotMapping && isArrayType(slotMapping)) {
        slotMapping.forEach((item) => {
            mapping.addSize(item[0], item[1]);
        });
        return mapping.build() || [];
    } else {
        return mapping.addSize([0, 0], slotSizes).addSize([1000, 200], slotSizes).build() || [];
    }
};

/**
 * Uzupełnienie jednostki reklamowej o mapping, targeting, słowa kluczowe.
 *
 * @param {googletag.Slot} definedAdUnit - zdefiniowana jednostka reklamowa
 * @param {string} placementId - nazwa kontenera - div id
 * @param {string[]} customKeywords - słowa kluczowe dla danego slotu
 * @param {googletag.SizeMappingArray} mapping - przygotowany mapping dla slotu
 * @param {string} slotId - slotId
 * @returns {googletag.Slot} adUnit
 */
const prepareDefinedAdUnit = (
    definedAdUnit: googletag.Slot,
    placementId: string,
    customKeywords: string[],
    mapping: googletag.SizeMappingArray,
    slotId: string
): googletag.Slot => {
    if (customKeywords.length) {
        definedAdUnit = definedAdUnit.setTargeting('keywordsSlot', customKeywords);
    }

    if (mapping.length) {
        definedAdUnit = definedAdUnit.defineSizeMapping(mapping);
    }

    definedAdUnit = definedAdUnit.setTargeting('pos', [slotId]);

    const stat = ['div_00'];
    if (placementId.indexOf('_') !== -1) {
        const arr = placementId.split('_');
        const adIndex = Number(arr[arr.length - 1]);
        if (!isNaN(adIndex)) {
            stat[0] = `div_${adIndex <= 9 ? '0' + adIndex : adIndex}`;
        }
    }
    definedAdUnit = definedAdUnit.setTargeting('stat_iu', stat);

    return definedAdUnit;
};

/**
 * Funkcja ustala słowa kluczowe będące parametrami targetingu slotu oraz mapping wykorzystując buildMapping z GPT.
 *
 * @param {IDFPServiceSlot} slot - slot
 * @returns { customKeywords: string[]; slotMapping: googletag.SizeMappingArray } - obiekt z ustawieniami path, customKeywords, mappingiem
 */
export const getSlotSettings = (slot: IDFPServiceSlot): { customKeywords: string[]; slotMapping: googletag.SizeMappingArray } => {
    let customKeywords: string[] = [];
    if (slot.keywords && isStringType(slot.keywords)) {
        const str = slot.keywords.replace(/\s/g, '');
        customKeywords = str.split(',');
    }

    const slotMapping: googletag.SizeMappingArray = slot.mapping ? buildMapping(slot.mapping, slot.sizes) : [];

    return {
        customKeywords,
        slotMapping,
    };
};

/**
 * Funkcja odpowiedzialna za tworzenie konkretnego miejsce reklamowego (tworzy jednostkę reklamową dla wskazanego placementu). Przypisuje usługi GPT.
 *
 * defineSlot: Definiuje miejsce na reklamę.
 * pubads: Ustawia konfigurację i zarządza zachowaniem reklam.
 *
 * @param {IDFPServiceSlot} slot - slot
 * @param {string} placementId - id kontenera DOM, w którym załadowana ma być reklama
 * @param {string[]} customKeywords - słowa kluczowe dla targetingu
 * @param {googletag.SizeMappingArray} slotMapping - mapping reklamy
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 *
 * @returns { Promise<string> } zwraca nazwę zdefiniowanego placementu/jednostki
 */
export const defineAdUnit = async (
    slot: IDFPServiceSlot,
    placementId: string,
    customKeywords: string[],
    slotMapping: googletag.SizeMappingArray,
    targeting: TargetingType | null
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const { detection, services, testConfig } = window.Inpl.Ad;
        const { adUnits, mapping } = services?.DFPService as IDFPService;
        const { defineSlot, defineOutOfPageSlot, pubads, enums } = googletag;

        // path do jednostki reklamowej,
        const path: string = `${window.Inpl.Ad.config?.adPath}/${slot.slotid}`;
        slot.path = path;

        const isPlacementDivExist: HTMLElement | null = document.getElementById(placementId);
        if (!isPlacementDivExist) {
            return reject(new Error(`Failed for placement: ${placementId} - missing placement in DOM.`));
        }

        const isAdUnitDefined: boolean = Object.hasOwn(adUnits, placementId);
        if (isAdUnitDefined) {
            return reject(new Error(`Failed for placement: ${placementId} - adUnit is already exists & defined.`));
        }

        // upewnienie się czy miejsce osadzenia istnieje w DOM i czy adUnit dla wskazanego placementu nie został zdefiniowany prędzej
        if (isPlacementDivExist && !isAdUnitDefined) {
            googletag.cmd.push(() => {
                let definedAdUnit: googletag.Slot | null = null;

                switch (true) {
                    case slot.name === 'interstitial': {
                        definedAdUnit = defineOutOfPageSlot(path, enums.OutOfPageFormat.INTERSTITIAL);
                        break;
                    }
                    case slot.name === 'bottom_anchor' && detection?.isMobile:
                        definedAdUnit = defineOutOfPageSlot(path, enums.OutOfPageFormat.BOTTOM_ANCHOR);
                        break;
                    case slot.outOfPage &&
                        !(slot.name === 'sitebranding' && document.getElementById(placementId)?.dataset?.adSitebrandingPh):
                        definedAdUnit = defineOutOfPageSlot(path, placementId);
                        break;
                    default: {
                        const sizesToDefined = !slot.sizes || isEmpty(slot.sizes) ? [] : slot.sizes;
                        definedAdUnit = defineSlot(path, sizesToDefined, placementId);
                        if (definedAdUnit) {
                            mapping[placementId] = slotMapping;
                            definedAdUnit = prepareDefinedAdUnit(definedAdUnit, placementId, customKeywords, slotMapping, slot.slotid);
                        }
                        break;
                    }
                }

                if (definedAdUnit) {
                    adUnits[placementId] = definedAdUnit;
                    adUnits[placementId].addService(pubads());

                    if (slot.name === 'interstitial') {
                        services?.TesterService?.isEnabled &&
                            adUnits[placementId].setConfig({
                                interstitial: {
                                    triggers: {
                                        navBar: false,
                                        unhideWindow: false,
                                    },
                                },
                            });

                        testConfig?.testInterstitial && adUnits[placementId].setTargeting('test', 'testerInterstitial');
                    }

                    if (targeting) {
                        adUnits[placementId].setTargeting(...targeting);
                    }
                    return resolve(placementId);
                } else {
                    reject(new Error(`Failed for placement: ${placementId}. Problem with execute GPT - defineSlot`));
                }
            });
        }
    });
};

/**
 * Obsługa osadzenia slotów dopiero w momencie pojawienia się na ekranie.
 * Metoda przekazuje sloty do renderowania w momencie gdy będą widoczne na stronie lub zbliżać się do viewport w zależności od lazyLoadingPosition.
 * Tworzy obserwer na kontenerze reklamy uwzględniając wartość lazy loadingu.
 *
 * @param {IDFPServiceSlot} slot - slot do obserwacji i wywołania osadzenia gdy pojawi się w widoku
 * @param {string[]} placementIdsToInsert - placementy gdzie osadzone mają być reklamy
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 * @param {boolean} setDefaultPlaces - opcjonalnie, informacja czy placement i observer ma być defaultowy - ad-[placement-id] & ad-view-[placement-id] czy placementy zostały przekazane bezpośrednio z serwisu
 *
 * @returns {void}
 */
export const embedAdSlotWhenIsVisible = (
    slot: IDFPServiceSlot,
    placementIdsToInsert: string[],
    targeting: TargetingType | null = null,
    setDefaultPlaces: boolean = false
): void => {
    const { UANC, exSettings } = window.Inpl.Ad;
    const { inView } = window.Inpl.Ad.services?.DFPService as IDFPService;

    placementIdsToInsert.forEach((placementId: string) => {
        try {
            let observeElementName = setDefaultPlaces ? placementId.replace('ad-', 'ad-view-') : placementId;

            // wyjątek dla iplv2, box_sidebar -> #82224
            if (slot.name === 'box_sidebar' && !exSettings?.designSystem) {
                observeElementName = `ad-view-${placementId}`;
            }

            let placementDOM = document.getElementById(observeElementName);

            // dla iplv2 może pojawić się przypadek, że nie ma ad-view-{slotName} - próba szukania po pierwotnym kontenerze ad-{slotName}
            if (!placementDOM && !exSettings?.designSystem && setDefaultPlaces) {
                observeElementName = placementId;
                placementDOM = document.getElementById(observeElementName);
            }
            if (!placementDOM) {
                throw new Error(`Failed for placement: ${observeElementName} - missing placement in DOM.`);
            }

            const executeEmbeddingCb = () => {
                if (!Object.hasOwn(inView, placementId)) {
                    Object.assign(inView, { [placementId]: true });
                    groupExecution(slot, [placementId], targeting); //przekazanie do zgrupowania w celu minimalizacji requestów
                }
            };

            observeElement(placementDOM, executeEmbeddingCb, slot.lazyLoadingPosition);
        } catch (error) {
            UANC instanceof DebugUANC &&
                UANC.warn(
                    `[embedAdSlotWhenIsVisible] Problem with define a slot: ${slot.name}. ${error instanceof Error ? error.message : error}`
                );
        }
    });
};

/**
 * Obsługa przypadku kiedy adSerwer nie zwrócił reklamy dla jednostki reklamowej.
 * Kasowanie adUnita, obsługa SmartLL, wysyłka eventu.
 *
 * @param {IDFPServiceSlot} slot - konfiguracja slotu
 * @param {string} adUnitName - nazwa jednostki
 *
 * @returns {void}
 */
export const handleEmptyAd = (slot: IDFPServiceSlot, adUnitName: string): void => {
    const { UANC } = window.Inpl.Ad;
    const { adUnits } = window.Inpl.Ad.services!.DFPService as IDFPService;

    // kasowanie adUnit
    googletag.cmd.push(() => googletag.destroySlots([adUnits[adUnitName]]));
    delete adUnits[adUnitName];
    UANC instanceof DebugUANC && UANC.warn(`[slotRenderEnded] adUnit: ${adUnitName} isEmpty - no ad has been rendered.`);

    // obsługa SmartLL
    const placementEl: HTMLElement | null = document.getElementById(adUnitName);
    if (slot.smartLL && slot.smartLLName && placementEl) {
        (placementEl as HTMLElement).id = slot.smartLLName;
        (document.getElementById(`ad-view-${slot.name}`) as HTMLElement).id = `ad-view-${slot.smartLLName}`;
        if (slot.smartIsLazyLoading) {
            window.Inpl.Ad.lazyLoadInsertAd?.(slot.smartLLName, [slot.smartLLName]);
        } else {
            window.Inpl.Ad.insertAd?.(slot.smartLLName, null, slot.smartLLName);
        }
    }

    addSlotEvent(slot, adUnitName, AD_STATUS.EMPTY);
};

/**
 * Zapisanie informacji o wyniku renderowania reklamy.
 * Ustawienie etykiety DSA, wysyłka informacji o załadowanej reklamie + przekazywane info do IWA.
 *
 * @param {IDFPServiceSlot} slot - konfiguracja slotu
 * @param {string} adUnitName - nazwa jednostki
 * @param {SlotRenderEndedEvent} renderDetails - informacje z serwera o reklamie
 *
 * @returns {void}
 */
const handleRenderedAd = (slot: IDFPServiceSlot, adUnitName: string, renderDetails: SlotRenderEndedEvent): void => {
    const { UANC } = window.Inpl.Ad;

    // zapis informacji o reklamie w renderedPlacements
    slot['renderedPlacements'] = {
        ...(slot['renderedPlacements'] ?? {}),
        [adUnitName]: {
            advertiserId: renderDetails.advertiserId,
            campaignId: renderDetails.campaignId,
            creativeId: renderDetails.creativeId,
            isDisplayed: true,
            isEmpty: renderDetails.isEmpty,
            currentSize: renderDetails.size,
            path: renderDetails.slot.getAdUnitPath(),
            bids: null,
        },
    };

    // tworzy etykietę DSA
    dsaGAM(slot, adUnitName);

    if (slot.name.startsWith('artspons') || slot.name.startsWith('m_artspons')) {
        addSlotEvent({ ...slot, dfp: true }, null, AD_STATUS.ART_SPONS);
    }
    addSlotEvent(slot, adUnitName, AD_STATUS.LOADED);
    UANC instanceof DebugUANC &&
        UANC.success(`[slotRenderEnded] Placement "${adUnitName}" for slot: "${slot.name}" was successfully rendered.`);
};

/**
 * Obsługa eventu GPT 'slotRequested' - informacja o wysłanym zapytaniu do adSerwera o kreację. Przekazywane info do IWA.
 * Wywoływane również przy odświeżaniu slotu.
 *
 * @param {SlotRequestedEvent} event - googletag event
 * @returns {void}
 */
export const handleSlotRequested = (event: SlotRequestedEvent): void => {
    const { config, testAB, services } = window.Inpl.Ad;
    const adUnitName = event.slot.getSlotElementId();
    const slot = <IDFPServiceSlot>findSlotByAdUnitName(adUnitName, services?.DFPService?.slots ?? []);

    if (slot && services?.DFPService?.adUnits && adUnitName in services.DFPService.adUnits) {
        const isSlotMultiPlaces = MULTI_PLACE_SLOTS.includes(slot.name);
        iwaAddEvent(
            'trackEvent',
            'uanc',
            'slots',
            'request',
            slot.name,
            isSlotMultiPlaces ? adUnitName : '',
            testAB?.currentTest ?? '',
            testAB?.currentTest ? JSON.stringify(config!.keywords) : ''
        );
    }
};

/**
 * Obsługa eventu GPT 'slotRenderEnded' - informacja o zakończonym renderowaniu slotu (reklama została załadowana i wyświetlona / slot okazał się pusty).
 * Wywoływane również przy odświeżaniu slotu.
 *
 * @param {SlotRequestedEvent} event - googletag event
 * @returns {void}
 */
export const handleSlotRenderEnded = (event: SlotRenderEndedEvent): void => {
    const { DFPService } = window.Inpl.Ad.services as IServices;

    const adUnitName = event.slot.getSlotElementId();
    const slot = <IDFPServiceSlot>findSlotByAdUnitName(adUnitName, DFPService?.slots ?? []);
    if (slot && DFPService?.adUnits && adUnitName in DFPService.adUnits) {
        // slot jest pusty, żadna reklama nie została wyświetlona
        if (event.isEmpty) {
            handleEmptyAd(slot, adUnitName);
        } else {
            handleRenderedAd(slot, adUnitName, event);
        }
    }
};

/**
 * Obsługa zdefiniowanych jednostek reklamowych. Wywołuje prebid, ładowanie reklamy i zbieranie końcowych informacji po renderowaniu reklamy.
 *
 * @param { IDefinedAdUnits} definedAdUnits - sloty z nazwami zdefiniowanych placementów / jednostek reklamowych
 * @returns {void}
 */
export const renderDefinedAdUnits = async (definedAdUnits: IDefinedAdUnits): Promise<void> => {
    const { UANC, services } = window.Inpl.Ad;
    const { DFPService } = services as IServices;

    if (!Object.keys(definedAdUnits).length) {
        UANC instanceof DebugUANC && UANC.warn('[renderDefinedAdUnits] Brak zdefiniowanych jednostek reklamowych.');
        return;
    }

    const adUnitsToRequest: Slot[] = [];
    const adUnitsToRequestWithoutPrebid: Slot[] = [];
    for (const [key, value] of Object.entries(definedAdUnits)) {
        value.forEach((adUnitName) => {
            if (adUnitName in (DFPService as IDFPService).adUnits) {
                const adUnit = (DFPService as IDFPService).adUnits[adUnitName];
                const withPrebid = (DFPService as IDFPService).slots.find((slot) => slot.name === key)?.prebid;

                withPrebid ? adUnitsToRequest.push(adUnit) : adUnitsToRequestWithoutPrebid.push(adUnit); // grupowanie jednostek dla wywołania jednego zapytania prebid
            }
        });
    }

    //załadowanie reklamy bez prebida
    googletag.cmd.push(() => {
        googletag.pubads().refresh(adUnitsToRequestWithoutPrebid);
    });

    await requestForBids(definedAdUnits);
    // załadowanie reklamy
    googletag.cmd.push(() => {
        googletag.pubads().refresh(adUnitsToRequest);
    });
};

/**
 * Inicjuje proces osadzania reklam na podstawie konfiguracji pobranych z ADC.
 * Główne przeznaczenie funkcji dla serwisów iplv2 gdzie osadzanie odbywa się od razu po zainicjowaniu adserwera.
 * Renderowanie obejmuje m.in. definiowane jednostek reklamowych, podłączenie usług GPT, ustawienie tartgetingu reklamy, obsłużenie prebida, żądanie reklamy, wyświetlenie.
 *
 * @returns {void}
 */
export const embeddingSlots = (): void => {
    const { DFPService } = window.Inpl.Ad.services as IServices;

    if (!DFPService?.slots.length) return;

    // not lazy loading slots
    const notLazyLoadingSlots: IDFPServiceSlot[] = getNotLazyLoadingSlots(DFPService.slots);
    const adsToEmbed: IAdsToEmbed = notLazyLoadingSlots.reduce((ads, slot) => {
        return {
            ...ads,
            [slot.name]: [
                {
                    placementIds: slot.placementIds,
                },
            ],
        };
    }, {});
    serviceDFPUtils.insertAds(adsToEmbed);

    // lazy loading slots
    const lazyLoadingSlots: IDFPServiceSlot[] = getLazyLoadingSlots(DFPService.slots);
    const lazyLoadingSlotsToDefined: IDFPServiceSlot[] = verifySlotsToDefined(lazyLoadingSlots);
    lazyLoadingSlotsToDefined.length &&
        lazyLoadingSlotsToDefined.forEach((slot: IDFPServiceSlot) => {
            embedAdSlotWhenIsVisible(slot, slot.placementIds, null, true);
        });
};
